import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { track } from '@citibox-panel/analytics';
import { Box, Button, Stack, Skeleton } from '@citibox-panel/ui';
import {
  ApiError,
  type BoxMarks,
  type BoxStatus,
  type ConfirmLockSequenceInput,
  type DeliveryDetail,
  type LocationMatch,
  type LockCodes,
  type LockerLog,
  type RecipientChange,
  type UserSearchHit,
} from '@citibox-panel/api-client';
import { useDeliveryDetail } from '../hooks/useDeliveryDetail';
import { useConfirmDeliveryCollect } from '../hooks/useConfirmDeliveryCollect';
import { useEnableDelivery } from '../hooks/useEnableDelivery';
import { useNonCollectDelivery } from '../hooks/useNonCollectDelivery';
import { useNonRetrieveDelivery } from '../hooks/useNonRetrieveDelivery';
import { useRejectDelivery } from '../hooks/useRejectDelivery';
import { useRetrieveDelivery } from '../hooks/useRetrieveDelivery';
import { useExtendCustody } from '../hooks/useExtendCustody';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { deliveryActionFlow, initialDeliveryActionState } from '../model/deliveryActionFlow';
import { extendCustodyContext } from '../lib/extendCustodyContext';
import type { ActionAudience } from './AudienceSelect';
import { DeliveryActionsCard } from './DeliveryActionsCard';
import { DeliverySharingCard } from './DeliverySharingCard';
import { ChangeRecipientDialog } from './ChangeRecipientDialog';
import { useChangeRecipient } from '../hooks/useChangeRecipient';
import { isRecipientChangeable } from '../lib/isRecipientChangeable';
import { changeRecipientError } from '../lib/changeRecipientError';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import { DeliveryDetailCard } from './DeliveryDetailCard';
import { ExtendCustodyControl } from './ExtendCustodyControl';
import {
  useAddressMismatch,
  type CheckAddressMismatchReport,
  type ReportAddressMismatch,
} from '../hooks/useAddressMismatch';

// Its own notice id: a failed reassignment and a failed row action are different sentences about
// different things, and one must not silently replace the other.
const RECIPIENT_NOTICE_ID = 'change-recipient';

export interface DeliveryLayoutProps {
  deliveryId: string;
  fetchDeliveryDetail: (id: string) => Promise<DeliveryDetail>;
  confirmDeliveryCollect: (id: string) => Promise<void>;
  enableDelivery: (id: string) => Promise<void>;
  nonCollectDelivery: (id: string) => Promise<void>;
  nonRetrieveDelivery: (id: string) => Promise<void>;
  rejectDelivery: (id: string) => Promise<void>;
  retrieveDelivery: (id: string) => Promise<void>;
  // The only write that carries a payload: the new expiration as a bare `YYYY-MM-DD` day.
  extendCustody: (id: string, expirationDate: string) => Promise<void>;
  // Two ids and nothing else — the panel resolves the phone and sends the account it found.
  changeDeliveryRecipient: (id: string, change: RecipientChange) => Promise<void>;
  // The header's own read, handed down so the dialog resolves a phone through the same seam (and
  // the same cache) the agent's header search filled.
  searchUsersByPhone: (phone: string) => Promise<UserSearchHit[]>;
  // The header's address search, handed down for «Notificar mismatch»: it places the titular's
  // address in its ubicación, through the same seam and cache the header and the ficha use.
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  fetchLockerLog: (boxId: string) => Promise<LockerLog>;
  fetchBoxStatus: (boxId: string, deliveryId: string) => Promise<BoxStatus>;
  fetchBoxMarks: (boxId: string) => Promise<BoxMarks>;
  clearBoxMarks: (boxId: string) => Promise<void>;
  // Read LOCK-first, unlike the two above: the code sequences belong to the cerradura, not to the
  // «buzón» it happens to be paired with today.
  fetchLockCodes: (lockId: string) => Promise<LockCodes>;
  confirmLockSequence: (lockId: string, input: ConfirmLockSequenceInput) => Promise<void>;
  legacyBoxMaintenanceUrl?: (boxId: string) => string | null;
  defaultAudience?: ActionAudience;
  // «Notificar mismatch»'s send, to the Google Sheet shared with Amazon. Absent, the warning shows
  // without its button; the panel's host always passes it, and an environment with no sheet answers
  // the send with the «sheet not found» notice.
  reportAddressMismatch?: ReportAddressMismatch;
  // Whether a tracking is already in that sheet, asked before any click: the button is offered only
  // while it is not. Absent, the button is offered and the send's own duplicate check is the guard.
  checkAddressMismatchReport?: CheckAddressMismatchReport;
}

function DeliveryDetailSkeleton() {
  return (
    // Unpadded, like the detail it stands in for: the gutter is the host's, and padding here too
    // would inset the skeleton twice and let the content jump inwards-then-out as it lands.
    <Box>
      <Stack spacing={1}>
        <Skeleton width={120} height={32} />
        <Skeleton width="60%" />
        <Skeleton width="40%" />
        <Skeleton width="80%" />
        <Skeleton width="80%" />
      </Stack>
    </Box>
  );
}

// Orchestrates the «Entrega» detail sub-tab: owns the query (fetch injected), the seven write
// mutations and the 5 s banner timer, so flow state — including the date slot of the one
// parameterized action — survives the skeleton remount during the success refetch.
// TWO INSTANCES of the same reducer, because the actions live in two places on screen:
//   · `cardState` — the six «Acciones» rows, whose mutual exclusion falls out of its single
//     `action` slot;
//   · `extendState` — «Ampliar custodia», which hangs off the «Expira la recogida» milestone in the
//     Trazabilidad (SPEC D1, reversed 2026-08-07). It is physically a separate control, so folding
//     it into the card's slot would blank the «Acciones» card while the agent picks a day. The two
//     flows are independent by construction; submitting a card action refetches the detail, which
//     remounts the timeline and folds the calendar away on its own.
// Renders the loading skeleton (initial load OR the post-write refetch) or the two sibling cards —
// information (flex 2) beside «Acciones» (flex 1). A fetch failure is thrown to the shell's per-sub-tab
// FeatureErrorBoundary by useDeliveryDetail, since the whole sub-tab is this one detail.
export function DeliveryLayout({
  deliveryId,
  fetchDeliveryDetail,
  confirmDeliveryCollect,
  enableDelivery,
  nonCollectDelivery,
  nonRetrieveDelivery,
  rejectDelivery,
  retrieveDelivery,
  extendCustody,
  changeDeliveryRecipient,
  searchUsersByPhone,
  searchLocations,
  fetchLockerLog,
  fetchBoxStatus,
  fetchBoxMarks,
  clearBoxMarks,
  fetchLockCodes,
  confirmLockSequence,
  legacyBoxMaintenanceUrl,
  defaultAudience = 'user',
  reportAddressMismatch,
  checkAddressMismatchReport,
}: DeliveryLayoutProps) {
  const queryClient = useQueryClient();
  // The shell's own seam for opening a «Usuario» tab. Reached from here the same way
  // `BoxDeliveriesShortcut` reaches it for a ubicación — the registered
  // `delivery-detail->search-shell` edge, and the only way a tab can be opened at all.
  const { activeKey, searches, moveDeliveryToSearch } = useSearchTabs();
  const { detail, isPending, isFetching } = useDeliveryDetail(deliveryId, fetchDeliveryDetail);
  const [state, dispatch] = useReducer(deliveryActionFlow, initialDeliveryActionState);
  const [extendState, extendDispatch] = useReducer(deliveryActionFlow, initialDeliveryActionState);
  const [awaitingRefetch, setAwaitingRefetch] = useState(false);
  // «Cambiar titular» is ONE step, and it is the dialog's: «Guardar» resolves, warns and writes.
  // It runs outside the card's reducer entirely — nothing about it is a row, a phase or a slot —
  // which is why the card below knows nothing of it beyond hosting the section its trigger hangs
  // off.
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  // The write's failure sentence, on its way to the shell's floating notice. Held here and not in
  // the card because this write no longer runs through the card's flow at all — the dialog both
  // chooses and commits.
  const [recipientFailure, setRecipientFailure] = useState<string | null>(null);
  // The box-log unfold lives here, ABOVE the whole-card skeleton's early return, so it survives
  // the skeleton remount during a post-write refetch — reopen the detail and the log is still open.
  const [logOpen, setLogOpen] = useState(false);
  const toggleLog = useCallback(() => {
    setLogOpen((open) => !open);
  }, []);
  // The «Acciones» card's audience filter lives here for the same reason as `logOpen`: the success
  // refetch unmounts the card, and the agent who just confirmed a courier action must come back to
  // the «Mensajero» list, not be bounced to «Usuario». The OPENING audience comes from the caller
  // — a delivery opened from a building is a courier case — and it is read once, on mount: the
  // prop describes where this sub-tab was opened from, which a tab cannot change while it lives.
  const [audience, setAudience] = useState<ActionAudience>(defaultAudience);
  // CANCEL unconditionally: the reducer's guard makes it a no-op outside `confirming`, and a
  // half-composed confirmation must never survive hidden behind the other audience's list.
  const handleAudienceChange = useCallback((next: ActionAudience) => {
    dispatch({ type: 'CANCEL' });
    setAudience(next);
  }, []);
  const confirmMutation = useConfirmDeliveryCollect(deliveryId, confirmDeliveryCollect);
  const enableMutation = useEnableDelivery(deliveryId, enableDelivery);
  const nonCollectMutation = useNonCollectDelivery(deliveryId, nonCollectDelivery);
  const nonRetrieveMutation = useNonRetrieveDelivery(deliveryId, nonRetrieveDelivery);
  const rejectMutation = useRejectDelivery(deliveryId, rejectDelivery);
  const retrieveMutation = useRetrieveDelivery(deliveryId, retrieveDelivery);
  const extendMutation = useExtendCustody(deliveryId, extendCustody);
  // The label the sub-tab already carries, so the «Entrega» arrives on the new titular's tab named
  // the way the agent has been reading it rather than renamed by the move.
  const currentDeliveryLabel = searches
    .find((tab) => tab.key === activeKey)
    ?.deliveries.find((d) => d.id === deliveryId)?.label;

  const changeRecipientMutation = useChangeRecipient(deliveryId, changeDeliveryRecipient);
  // Straight to the shell's notice, like every other write failure since CBX-11349 — it survives
  // this card's remount on the refetch, and the dialog stays usable underneath it.
  useReportFailure(RECIPIENT_NOTICE_ID, recipientFailure, () => {
    setRecipientFailure(null);
  });

  // «Notificar mismatch»: read, comparison, flow, send and events, composed into one node. Called
  // here, above the skeleton a refetch raises, so its state survives the remount (ADR 0013).
  const addressMismatchAlert = useAddressMismatch(
    deliveryId,
    detail,
    searchUsersByPhone,
    searchLocations,
    reportAddressMismatch,
    checkAddressMismatchReport,
  );

  // Hold the skeleton over the whole card until the post-write refetch has actually run
  // (isFetching went true then false). The ref prevents a premature clear in the render before
  // the refetch starts.
  const sawRefetch = useRef(false);
  useEffect(() => {
    if (!awaitingRefetch) return;
    if (isFetching) {
      sawRefetch.current = true;
      return;
    }
    if (sawRefetch.current) {
      sawRefetch.current = false;
      setAwaitingRefetch(false);
    }
  }, [awaitingRefetch, isFetching]);

  // SUCCESS ONLY, on both flows. A failure no longer waits out a timer here: it floats (ADR 0021)
  // and the control that reported it hands the flow straight back, so the action is pressable again
  // while the notice stays up until the agent dismisses it — which is what the `persistent` flag
  // used to buy for the case-specific causes. Success waits until its refetch is done, so the 5 s
  // counts from when the banner is actually visible, not while the skeleton is up. One rule, two
  // flows → one hook.
  useAutoDismiss(state.phase === 'success' && !awaitingRefetch, dispatch);
  useAutoDismiss(extendState.phase === 'success' && !awaitingRefetch, extendDispatch);

  // A stale-state conflict is the one error worth refetching for: the reappearing actions must
  // reflect the real state rather than a view we already know is out of date. Any other cause (a
  // Carrier that forbids extending, a devolución) is not fixed by refetching. The notice the agent
  // may be reading to the «Usuario» survives either way now — it lives in the shell, not in the
  // subtree this invalidation remounts.
  // TWO codes now, and the second is the reason «Cambiar titular» exists: `delivery_state_conflict`
  // says the delivery moved on, `delivery_recipient_conflict` says ANOTHER AGENT moved the titular
  // between this agent reading the card and confirming. Both mean the view is provably out of date,
  // and both are answered the same way — refetch, so what reappears is real. Any other cause (a
  // Carrier that forbids extending, a devolución, an account that cannot hold a parcel) is not
  // fixed by refetching. The notice the agent may be reading to the «Usuario» survives either way:
  // it lives in the shell, not in the subtree this invalidation remounts.
  const refetchOnStateConflict = (code: string | null) => {
    if (code === 'delivery_state_conflict' || code === 'delivery_recipient_conflict') {
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail', deliveryId] });
    }
  };

  // Four writes share one policy — non-collect, reject and the two «Mensajero» ones: on success show
  // the refetch skeleton then the banner; on ANY error show the banner AND refetch the detail so the
  // reappearing actions reflect the real state (code-agnostic — none of them maps an error code,
  // unlike restore).
  const mutateWithRefetchOnError = (mutation: typeof nonCollectMutation) => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        setAwaitingRefetch(true);
        dispatch({ type: 'SUCCESS' });
      },
      onError: () => {
        dispatch({ type: 'ERROR', code: null });
        void queryClient.invalidateQueries({ queryKey: ['delivery-detail', deliveryId] });
      },
    });
  };

  // «Ampliar custodia» runs its own flow, so it gets its own submit. Same policy as restore:
  // case-specific error copy per code, and a refetch only on a stale-state conflict.
  const onConfirmExtend = () => {
    // Unreachable — START seeds the floor, so the parameterized action always has its payload. Here
    // so the flow stays total instead of hanging in `pending` if that ever stops holding.
    if (extendState.date === null) return;
    extendDispatch({ type: 'SUBMIT' });
    extendMutation.mutate(extendState.date, {
      onSuccess: () => {
        setAwaitingRefetch(true);
        extendDispatch({ type: 'SUCCESS' });
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : null;
        extendDispatch({ type: 'ERROR', code });
        refetchOnStateConflict(code);
      },
    });
  };

  const onConfirm = () => {
    dispatch({ type: 'SUBMIT' });
    if (state.action === 'restore') {
      enableMutation.mutate(undefined, {
        onSuccess: () => {
          setAwaitingRefetch(true);
          dispatch({ type: 'SUCCESS' });
        },
        onError: (err) => {
          const code = err instanceof ApiError ? err.code : null;
          dispatch({ type: 'ERROR', code });
          refetchOnStateConflict(code);
        },
      });
    } else if (state.action === 'nonCollect') {
      mutateWithRefetchOnError(nonCollectMutation);
    } else if (state.action === 'reject') {
      mutateWithRefetchOnError(rejectMutation);
    } else if (state.action === 'retrieve') {
      mutateWithRefetchOnError(retrieveMutation);
    } else if (state.action === 'nonRetrieve') {
      mutateWithRefetchOnError(nonRetrieveMutation);
    } else {
      confirmMutation.mutate(undefined, {
        onSuccess: () => {
          setAwaitingRefetch(true);
          dispatch({ type: 'SUCCESS' });
        },
        onError: () => {
          dispatch({ type: 'ERROR', code: null });
        },
      });
    }
  };

  if (isPending || awaitingRefetch) return <DeliveryDetailSkeleton />;
  if (!detail) return null;

  // Resolved HERE and not in the timeline: this predicate needs the «buzón» timezone and the two
  // contract gates, which is orchestrator business. `null` means the delivery is not extendable, so
  // no control is handed down at all and the milestone renders bare.
  const extendContext = extendCustodyContext({
    status: detail.status,
    estimatedExpirationDate: detail.estimatedExpirationDate,
    lockerTimezone: detail.location.timezone,
    isReturn: detail.isReturn,
    // FAIL-OPEN BY CONSTRUCTION (CBX-10954): the policy rides inside a nullable `carrier`, so a
    // delivery with no transportista carries none — and `null` is what the predicate reads as
    // «offer the action». The saved WRITE defaults the same way, so the ampliación is granted
    // instead of being rejected after the screen offered it.
    carrierAllowsExtension: detail.carrier?.allowsExtension ?? null,
  });

  // Wired here and handed down as one node: the layout owns the flow, and everything between it and
  // the milestone (the information card, the trazabilidad) stays ignorant of «Ampliar custodia».
  const extendCustodyControl =
    extendContext === null ? undefined : (
      <ExtendCustodyControl
        context={extendContext}
        state={extendState}
        onStart={(date) => {
          extendDispatch({ type: 'START', action: 'extendCustody', date });
        }}
        onCancel={() => {
          extendDispatch({ type: 'CANCEL' });
        }}
        onConfirm={onConfirmExtend}
        onFailureReported={() => {
          extendDispatch({ type: 'DISMISS' });
        }}
        onSetDate={(date) => {
          extendDispatch({ type: 'SET_DATE', date });
        }}
      />
    );

  // Built here and handed down as one node, exactly like `extendCustodyControl` above: the
  // «Acciones» card stays ignorant of titulares and authorized pickups.
  //
  // Passed UNCONDITIONALLY. The «is it shared» question used to be asked twice — here and inside
  // the section — and this half existed to keep the card from drawing a full-bleed rule above a
  // component that would then render nothing, leaving a stray line across the card. The section
  // always renders now (it holds the titular), so there is no such case left and the rule always
  // has something under it. What is still conditional lives inside the section, where it belongs:
  // the «COMPARTIDO CON» half, and the heading that names it.
  // The trigger is built HERE and handed down as one wired node, exactly like
  // `extendCustodyControl`: the section decides where it goes, this decides whether it is offered
  // at all. `isRecipientChangeable` is fail-closed, so an unrecognized state offers nothing.
  //
  // `variant="text" sentenceCase`, not `inline`: `inline` is only legible «where the frame around
  // the control already says this is actionable» (its own note), and here the action floats in the
  // card's open space with no frame around it. Same look as the «Cambiar» of the address filters,
  // the closest thing the product already has to this.
  //
  // `bold` on top of that, which the address filters' «Cambiar» does not carry: this one is the only
  // control in its own section, so weight is what separates it from the two lines of titular detail
  // beside it rather than letting it read as a third one.
  const changeRecipientAction = isRecipientChangeable(detail.status) ? (
    <Button
      variant="text"
      sentenceCase
      bold
      size="small"
      onClick={() => {
        setRecipientDialogOpen(true);
      }}
    >
      Cambiar titular
    </Button>
  ) : undefined;

  const sharingSection = (
    <DeliverySharingCard
      recipient={detail.recipient}
      authorized={detail.sharedWith}
      {...(changeRecipientAction !== undefined ? { changeRecipientAction } : {})}
    />
  );

  return (
    // No outer margin: the gutter belongs to whoever mounts this, because a parent that lays its
    // children out with spacing resets a child's margin and the loss is invisible from in here.
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flex: 1 }}>
      <ChangeRecipientDialog
        open={recipientDialogOpen}
        onClose={() => {
          setRecipientDialogOpen(false);
        }}
        onSave={(person) => {
          // «Guardar» IS the confirmation — there is no second step behind it, so this writes.
          changeRecipientMutation.mutate(
            {
              userId: person.id,
              // The titular the card is showing THE MOMENT the agent presses. Read here rather
              // than when they chose, because a background refetch that landed in between has
              // already repainted the card — so this is what they are looking at. It guards
              // against the change they could NOT see: another agent moving the titular
              // server-side, which backend refuses instead of letting this write bury it.
              expectedCurrentUserId: detail.recipient?.id ?? null,
            },
            {
              onSuccess: () => {
                // Dimensions ABOUT the reassignment, never its subjects. Neither person on this
                // screen reaches telemetry: no phone, no name, and not the ids either — an id is a
                // handle on a person, so it is PII with extra steps.
                track('delivery_recipient_changed', {
                  had_shares: detail.sharedWith.length > 0 ? 'true' : 'false',
                });
                setRecipientDialogOpen(false);
                // The parcel changed hands, so the «Entrega» stops belonging on the tab it is being
                // read from. One operation and not a close plus an open: two navigations in a tick
                // do not compose, and the agent would end up with it on both tabs or on neither.
                // It also lands them where they have to call — backend notifies nobody.
                moveDeliveryToSearch(
                  activeKey ?? '',
                  person.phone,
                  person.name || undefined,
                  deliveryId,
                  currentDeliveryLabel,
                );
              },
              onError: (err) => {
                // The dialog STAYS, holding the account the agent just resolved: a retry is one
                // press instead of re-typing a phone. The sentence floats in the shell's notice,
                // which outlives this card's remount on the refetch (ADR 0021).
                const code = err instanceof ApiError ? err.code : null;
                setRecipientFailure(changeRecipientError(code));
                refetchOnStateConflict(code);
              },
            },
          );
        }}
        saving={changeRecipientMutation.isPending}
        authorizedCount={detail.sharedWith.length}
        searchUsersByPhone={searchUsersByPhone}
        currentRecipientId={detail.recipient?.id ?? null}
      />
      <DeliveryDetailCard
        detail={detail}
        logOpen={logOpen}
        onToggleLog={toggleLog}
        fetchLockerLog={fetchLockerLog}
        fetchBoxStatus={fetchBoxStatus}
        fetchBoxMarks={fetchBoxMarks}
        clearBoxMarks={clearBoxMarks}
        fetchLockCodes={fetchLockCodes}
        confirmLockSequence={confirmLockSequence}
        {...(legacyBoxMaintenanceUrl ? { legacyBoxMaintenanceUrl } : {})}
        {...(extendCustodyControl !== undefined ? { extendCustodyControl } : {})}
        {...(addressMismatchAlert !== undefined ? { addressMismatchAlert } : {})}
      />
      <DeliveryActionsCard
        status={detail.status}
        sharingSection={sharingSection}
        state={state}
        audience={audience}
        onAudienceChange={handleAudienceChange}
        onStart={(action) => {
          dispatch({ type: 'START', action });
        }}
        onCancel={() => {
          dispatch({ type: 'CANCEL' });
        }}
        onConfirm={onConfirm}
        onFailureReported={() => {
          dispatch({ type: 'DISMISS' });
        }}
      />
    </Box>
  );
}
